import { useEffect, useRef, useState } from 'react';
import { setLang, useT, type Lang } from '../i18n';

const ORDER: Lang[] = ['en', 'es', 'pt', 'fr', 'it', 'de', 'nl'];

const LANG_NAMES: Record<Lang, string> = {
  en: 'English',
  es: 'Español',
  nl: 'Nederlands',
  de: 'Deutsch',
  pt: 'Português',
  fr: 'Français',
  it: 'Italiano',
};

function FlagSvg({ lang }: { lang: Lang }) {
  // Flat SVG flags — render consistently across platforms (emoji flags
  // get rendered as glossy 3D shapes on Apple devices and as plain letter
  // pairs on Windows). 20×14 fits inside the 32 px circular button.
  const w = 20;
  const h = 14;
  switch (lang) {
    case 'es':
      return (
        <svg viewBox="0 0 6 4" width={w} height={h} aria-hidden="true">
          <rect width="6" height="1" y="0" fill="#aa151b" />
          <rect width="6" height="2" y="1" fill="#f1bf00" />
          <rect width="6" height="1" y="3" fill="#aa151b" />
        </svg>
      );
    case 'nl':
      return (
        <svg viewBox="0 0 3 2" width={w} height={h} aria-hidden="true">
          <rect width="3" height="0.667" y="0" fill="#ae1c28" />
          <rect width="3" height="0.666" y="0.667" fill="#fff" />
          <rect width="3" height="0.667" y="1.333" fill="#21468b" />
        </svg>
      );
    case 'de':
      return (
        <svg viewBox="0 0 3 2" width={w} height={h} aria-hidden="true">
          <rect width="3" height="0.667" y="0" fill="#000" />
          <rect width="3" height="0.667" y="0.667" fill="#dd0000" />
          <rect width="3" height="0.666" y="1.333" fill="#ffce00" />
        </svg>
      );
    case 'pt':
      return (
        <svg viewBox="0 0 5 3" width={w} height={h} aria-hidden="true">
          <rect width="2" height="3" x="0" fill="#006233" />
          <rect width="3" height="3" x="2" fill="#d52b1e" />
        </svg>
      );
    case 'fr':
      return (
        <svg viewBox="0 0 3 2" width={w} height={h} aria-hidden="true">
          <rect width="1" height="2" x="0" fill="#0055a4" />
          <rect width="1" height="2" x="1" fill="#fff" />
          <rect width="1" height="2" x="2" fill="#ef4135" />
        </svg>
      );
    case 'it':
      return (
        <svg viewBox="0 0 3 2" width={w} height={h} aria-hidden="true">
          <rect width="1" height="2" x="0" fill="#009246" />
          <rect width="1" height="2" x="1" fill="#fff" />
          <rect width="1" height="2" x="2" fill="#ce2b37" />
        </svg>
      );
    case 'en':
    default:
      return (
        <svg viewBox="0 0 39 26" width={w} height={h} aria-hidden="true">
          <rect width="39" height="26" fill="#fff" />
          {[0, 4, 8, 12, 16, 20, 24].map((y) => (
            <rect key={y} width="39" height="2" y={y} fill="#b22234" />
          ))}
          <rect width="16" height="14" fill="#3c3b6e" />
        </svg>
      );
  }
}

/**
 * Top-right language switcher — a small flag button sitting under the
 * help (`?`) button. Tap once to expand the list of available languages,
 * tap a flag to switch. Mode-agnostic: rendered from App.tsx so it shows
 * in both Cesium and Leaflet viewers.
 */
export function LanguageToggle() {
  const { lang } = useT();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = containerRef.current;
      if (el && e.target instanceof Node && el.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const buttonStyle = (active: boolean): React.CSSProperties => ({
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: active
      ? '1px solid rgba(34,197,94,0.9)'
      : '1px solid rgba(255,255,255,0.25)',
    background: 'rgba(0,0,0,0.62)',
    color: 'white',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: 0,
  });

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 92,            // sits right below the `?` help button (top: 52, height: 32 + 8 gap)
        right: 16,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 6,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        title="Language"
        style={buttonStyle(false)}
      >
        <FlagSvg lang={lang} />
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ORDER.filter((l) => l !== lang).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => {
                setLang(l);
                setOpen(false);
              }}
              aria-label={`Switch to ${LANG_NAMES[l]}`}
              title={LANG_NAMES[l]}
              style={buttonStyle(false)}
            >
              <FlagSvg lang={l} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
